<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import {
  parseDetailReportFilters,
  generateReportURLParams,
  generateSummaryEntityFilterParams,
} from './helpers/reportFilterHelper';
import ReportFiltersSection from './components/ReportFiltersSection.vue';
import SummaryDetailHeader from './components/SummaryDetailHeader.vue';
import SummaryDetailMetrics from './components/SummaryDetailMetrics.vue';
import SummaryEntityDetailCharts from './components/SummaryEntityDetailCharts.vue';
import SummaryDetailHeatmaps from './components/SummaryDetailHeatmaps.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const label = useFunctionGetter('labels/getLabelById', route.params.id);

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const selectedInboxIds = ref([]);
const selectedTeamIds = ref([]);
const selectedAgentIds = ref([]);
const resolutionMethod = ref('all');
const tagPresence = ref('all');

const applyEntityFilters = filters => {
  selectedInboxIds.value = filters.inboxIds || [];
  selectedTeamIds.value = filters.teamIds || [];
  selectedAgentIds.value = filters.agentIds || [];
  resolutionMethod.value = filters.resolutionMethod || 'all';
  tagPresence.value = filters.tagPresence || 'all';
};

const initializeFromURL = () => {
  const parsed = parseDetailReportFilters(route.query);
  from.value = parsed.from || 0;
  to.value = parsed.to || 0;
  businessHours.value = parsed.businessHours;
  applyEntityFilters(parsed);
};

initializeFromURL();

const detailFilters = computed(() => ({
  from: from.value,
  to: to.value,
  businessHours: businessHours.value,
  inboxIds: selectedInboxIds.value,
  teamIds: selectedTeamIds.value,
  agentIds: selectedAgentIds.value,
  labelIds: [Number(route.params.id)],
  resolutionMethod: resolutionMethod.value,
  tagPresence: tagPresence.value,
}));

const updateURLParams = () => {
  const entityParams = generateSummaryEntityFilterParams({
    inboxIds: selectedInboxIds.value,
    teamIds: selectedTeamIds.value,
    agentIds: selectedAgentIds.value,
    resolutionMethod: resolutionMethod.value,
    tagPresence: tagPresence.value,
  });
  const dateParams = generateReportURLParams({
    from: from.value,
    to: to.value,
    businessHours: businessHours.value,
  });
  router.replace({ query: { ...dateParams, ...entityParams } });
};

const onFiltersApply = ({ date, entity }) => {
  from.value = date.from;
  to.value = date.to;
  businessHours.value = date.businessHours;
  applyEntityFilters(entity);
  updateURLParams();
};

onMounted(() => store.dispatch('labels/get'));
watch(() => route.query, initializeFromURL);
</script>

<template>
  <div v-if="label.id" class="flex flex-col gap-4">
    <SummaryDetailHeader
      :title="label.title"
      report-type="label"
      :filters="detailFilters"
      :download-label="$t('LABEL_REPORTS.DOWNLOAD_LABEL_REPORTS')"
    />
    <ReportFiltersSection
      type="label"
      hide-label-filter
      @filters-apply="onFiltersApply"
    />
    <SummaryDetailMetrics
      type="label"
      :entity-id="label.id"
      :filters="detailFilters"
    />
    <SummaryEntityDetailCharts
      type="label"
      :entity-id="label.id"
      :filters="detailFilters"
    />
    <SummaryDetailHeatmaps
      entity-type="label"
      :entity-id="label.id"
      :filters="detailFilters"
    />
  </div>
  <div v-else class="w-full py-20">
    <Spinner class="mx-auto" />
  </div>
</template>
